import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import axios from "axios";
import BASE_URL from "../utils";

/* -------------------------------------------
   Auto-detect API URL
-------------------------------------------- */


/* -------------------------------------------
   Helper — Detect External Links
-------------------------------------------- */
const isExternalLink = (url = "") => {
  return url.startsWith("http://") || url.startsWith("https://");
};

const ANNOUNCEMENTS = [
  { text: "WELCOME TO THE GARDEN" },
  { text: "100% PLANT-BASED & ALLERGEN FREE" },
  { text: "THERE’S A NEW CHEESE IN TOWN" },
  {
    text: "DOWNLOAD OUR PLANT-BASED SWITCH APP",
    href: "https://apps.apple.com/in/app/the-plant-based-switch/id6444078958",
  },
];

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [menuData, setMenuData] = useState([]);
  const [announcementIndex, setAnnouncementIndex] = useState(0);

  const toggleDropdown = (menuId) => {
    setOpenDropdown(openDropdown === menuId ? null : menuId);
  };

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/menus/handle/header`);
        setMenuData(res.data.items || []);
      } catch (err) {
        console.error("Error fetching menu:", err);
      }
    };
    fetchMenu();
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      setAnnouncementIndex((current) => (current + 1) % ANNOUNCEMENTS.length);
    }, 3000);
    return () => window.clearInterval(id);
  }, []);

  const announcement = ANNOUNCEMENTS[announcementIndex];

  return (
    <header className="w-full">
      {/* ===== Announcement Bar ===== */}
      <div className="bg-[#c03ca0] text-white text-sm relative">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center h-8 relative">
          <p className="w-[90%] text-center truncate">
            {announcement.href ? (
              <a
                href={announcement.href}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
              >
                {announcement.text}
              </a>
            ) : (
              announcement.text
            )}
          </p>

          <button
            type="button"
            aria-label="Previous announcement"
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 cursor-pointer text-white text-base opacity-80 hover:opacity-100 z-20"
            onClick={() =>
              setAnnouncementIndex(
                (current) => (current + ANNOUNCEMENTS.length - 1) % ANNOUNCEMENTS.length
              )
            }
          >
            &#10094;
          </button>
          <button
            type="button"
            aria-label="Next announcement"
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 cursor-pointer text-white text-base opacity-80 hover:opacity-100 z-20"
            onClick={() =>
              setAnnouncementIndex((current) => (current + 1) % ANNOUNCEMENTS.length)
            }
          >
            &#10095;
          </button>
        </div>
      </div>

      {/* ===== Main Navbar ===== */}
      <div className="bg-white shadow-sm relative z-50">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-16">
          {/* Logo */}
          <a href="/" className="">
            <img src={`${process.env.PUBLIC_URL}/logo-nav.webp`} alt="Nafsikas garden logo" width="106" height="40" className="h-10 w-auto object-contain" />
          </a>

          {/* ==== Desktop Menu ==== */}
          <nav className="hidden md:flex space-x-8 text-gray-700 font-medium ml-auto relative z-50">
            {menuData.map((menu, index) => {
              const isLast = index === menuData.length - 1;
              const hasChildren = menu.children?.length > 0;

              return (
                <div key={menu._id} className="relative group">
                  {/* --- Top Level Menu --- */}
                  {hasChildren ? (
                    <div className="flex items-center cursor-pointer hover:text-[#c03ca0] transition-colors">
                      {menu.title}
                      <ChevronDown
                        size={16}
                        className="ml-1 transition-transform duration-200 group-hover:rotate-180"
                      />
                    </div>
                  ) : (
                    <a
                      href={menu.url}
                      target={isExternalLink(menu.url) ? "_blank" : "_self"}
                      rel={isExternalLink(menu.url) ? "noopener noreferrer" : undefined}
                      className="hover:text-[#c03ca0] transition-colors"
                    >
                      {menu.title}
                    </a>
                  )}

                  {/* --- Dropdown --- */}
                  {hasChildren && (
                    <div
                      className={`absolute top-full mt-2 bg-white shadow-lg rounded-md py-2 w-44 transition-all duration-200 opacity-0 invisible group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 translate-y-2 ${
                        isLast ? "right-0" : "left-0"
                      }`}
                    >
                      {menu.children.map((child, idx) => (
                        <a
                          key={idx}
                          href={child.url}
                          target={
                            isExternalLink(child.url) ? "_blank" : "_self"
                          }
                          rel={
                            isExternalLink(child.url)
                              ? "noopener noreferrer"
                              : undefined
                          }
                          className="block px-4 py-2 hover:bg-gray-100 whitespace-nowrap"
                        >
                          {child.title}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="md:hidden p-2"
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            aria-controls="mobile-menu"
            onClick={() => setDrawerOpen(true)}
          >
            <Menu size={24} />
          </button>
        </div>
      </div>

      {/* ===== Mobile Drawer ===== */}
      <div
        id="mobile-menu"
        inert={!drawerOpen}
        aria-hidden={!drawerOpen}
        className={`fixed top-0 right-0 h-full w-3/4 sm:w-1/2 bg-white shadow-lg z-50 transform transition-transform duration-300 ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex justify-between items-center p-4 border-b">
          <p className="text-lg font-semibold text-gray-800">Menu</p>
          <button type="button" aria-label="Close menu" onClick={() => setDrawerOpen(false)}>
            <X size={24} />
          </button>
        </div>

        <nav className="flex flex-col p-4 space-y-3 text-gray-700 font-medium">
          {menuData.map((menu) => (
            <div key={menu._id}>
              {menu.children?.length > 0 ? (
                <>
                  <button
                    onClick={() => toggleDropdown(menu._id)}
                    className="flex justify-between w-full"
                  >
                    {menu.title}
                    <ChevronDown size={16} />
                  </button>

                  {openDropdown === menu._id && (
                    <div className="ml-4 mt-2 space-y-2 text-sm flex flex-col">
                      {menu.children.map((child, idx) => (
                        <a
                          key={idx}
                          href={child.url}
                          target={
                            isExternalLink(child.url) ? "_blank" : "_self"
                          }
                          rel={
                            isExternalLink(child.url)
                              ? "noopener noreferrer"
                              : undefined
                          }
                          onClick={() => setDrawerOpen(false)}
                          className="block py-1 hover:text-[#c03ca0]"
                        >
                          {child.title}
                        </a>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <a
                  href={menu.url}
                  target={isExternalLink(menu.url) ? "_blank" : "_self"}
                  rel={
                    isExternalLink(menu.url) ? "noopener noreferrer" : undefined
                  }
                  onClick={() => setDrawerOpen(false)}
                  className="hover:text-[#c03ca0]"
                >
                  {menu.title}
                </a>
              )}
            </div>
          ))}
        </nav>
      </div>

      {/* Background Overlay */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40"
          onClick={() => setDrawerOpen(false)}
        ></div>
      )}
    </header>
  );
};

export default Header;





















// import React, { useState, useEffect } from "react";
// import { Menu, X, ChevronDown } from "lucide-react";
// import { Swiper, SwiperSlide } from "swiper/react";
// import { Autoplay, Navigation, EffectFade } from "swiper/modules";
// import "swiper/css";
// import "swiper/css/navigation";
// import "swiper/css/effect-fade";
// import logo from "../assets/logo.webp";
// import axios from "axios";

// let BASE_URL = "";
// if (process.env.REACT_APP_API_URL) {
//   BASE_URL = process.env.REACT_APP_API_URL;
// } else if (window.location.hostname === "localhost") {
//   BASE_URL = "https://admin.naf.network";
// } else {
//   BASE_URL = "https://admin.naf.network"; // your production admin base
// }
// // http://localhost:5001
// const Header = () => {
//   const [drawerOpen, setDrawerOpen] = useState(false);
//   const [openDropdown, setOpenDropdown] = useState(null);
//   const [menuData, setMenuData] = useState([]);

//   const toggleDropdown = (menuId) => {
//     setOpenDropdown(openDropdown === menuId ? null : menuId);
//   };

//   useEffect(() => {
//   const fetchMenu = async () => {
//     try {
//       // change "main" to whatever handle your menu has in admin
//       const res = await axios.get(`${BASE_URL}/api/menus/handle/header`);
//       console.log("MENU RESPONSE:", res.data);
//       setMenuData(res.data.items || []);
//     } catch (err) {
//       console.error("Error fetching menu:", err);
//     }
//   };
//   fetchMenu();
// }, []);


//   return (
//     <header className="w-full">
//       {/* ===== Announcement Bar ===== */}
//       <div className="bg-[#c03ca0] text-white text-sm relative">
//         <div className="max-w-7xl mx-auto px-4 flex items-center justify-center h-8 relative">
//           <Swiper
//             modules={[Autoplay, Navigation, EffectFade]}
//             autoplay={{
//               delay: 3000,
//               disableOnInteraction: false,
//               pauseOnMouseEnter: true,
//             }}
//             navigation={{
//               nextEl: ".swiper-button-next-custom",
//               prevEl: ".swiper-button-prev-custom",
//             }}
//             loop={true}
//             effect="fade"
//             fadeEffect={{ crossFade: true }}
//             allowTouchMove={true}
//             speed={600}
//             className="w-[90%] text-center select-none z-10"
//           >
//             <SwiperSlide>WELCOME TO THE GARDEN</SwiperSlide>
//             <SwiperSlide>100% PLANT-BASED & ALLERGEN FREE</SwiperSlide>
//             <SwiperSlide>THERE’S A NEW CHEESE IN TOWN</SwiperSlide>
//             <SwiperSlide>
//               <a
//                 href="https://apps.apple.com/in/app/the-plant-based-switch/id6444078958"
//                 target="_blank"
//                 rel="noopener noreferrer"
//                 className="hover:underline"
//               >
//                 DOWNLOAD OUR PLANT-BASED SWITCH APP
//               </a>
//             </SwiperSlide>
//           </Swiper>

//           <div className="swiper-button-prev-custom absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 cursor-pointer text-white text-base opacity-80 hover:opacity-100 z-20 select-none">
//             &#10094;
//           </div>
//           <div className="swiper-button-next-custom absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 cursor-pointer text-white text-base opacity-80 hover:opacity-100 z-20 select-none">
//             &#10095;
//           </div>
//         </div>
//       </div>

//       {/* ===== Main Navbar ===== */}
//       <div className="bg-white shadow-sm relative z-50">
//         <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-16">
//           <div className="flex items-center">
//             <a
//               href='/'
//               className=""
//             >
//             <img src={logo} alt="Logo" className="h-10 w-auto object-contain" />
//             </a>
//           </div>

//           {/* --- Desktop Menu --- */}
//           {/* --- Desktop Menu --- */}
// <nav className="hidden md:flex space-x-8 text-gray-700 font-medium ml-auto relative z-50">
//   {menuData.map((menu, index) => {
//     const isLast = index === menuData.length - 1;
//     const hasChildren = menu.children?.length > 0;

//     return (
//       <div
//         key={menu._id}
//         className="relative group"
//       >
//         {/* --- Top Level Item --- */}
//         {hasChildren ? (
//           <div className="flex items-center cursor-pointer hover:text-[#c03ca0] transition-colors">
//             {menu.title}
//             <ChevronDown
//               size={16}
//               className="ml-1 transition-transform duration-200 group-hover:rotate-180"
//             />
//           </div>
//         ) : (
//           <a
//             href={menu.url}
//             className="hover:text-[#c03ca0] transition-colors"
//           >
//             {menu.title}
//           </a>
//         )}

//         {/* --- Dropdown --- */}
//         {hasChildren && (
//           <div
//             className={`absolute top-full mt-2 bg-white shadow-lg rounded-md py-2 w-44 transition-all duration-200 opacity-0 invisible group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 translate-y-2 ${isLast ? "right-0" : "left-0"}`}
//           >
//             {menu.children.map((child, idx) => (
//               <a
//                 key={idx}
//                 href={child.url}
//                 className="block px-4 py-2 hover:bg-gray-100 whitespace-nowrap"
//               >
//                 {child.title}
//               </a>
//             ))}
//           </div>
//         )}
//       </div>
//     );
//   })}
// </nav>

//           {/* <nav className="hidden md:flex space-x-8 text-gray-700 font-medium ml-auto relative">
//             {menuData.map((menu) => (
//               <div
//                 key={menu._id}
//                 className="relative"
//                 onMouseEnter={() => setOpenDropdown(menu._id)}
//                 onMouseLeave={() => setOpenDropdown(null)}
//               >
//                 <button className="flex items-center hover:text-[#c03ca0]">
//                   {menu.title}
//                   {menu.children?.length > 0 && (
//                     <ChevronDown size={16} className="ml-1" />
//                   )}
//                 </button>

//                 {menu.children?.length > 0 && (
//                   <div
//                     className={`absolute left-0 mt-2 bg-white shadow-lg rounded-md py-2 w-44 transition-all duration-200 ${
//                       openDropdown === menu._id
//                         ? "opacity-100 visible translate-y-0"
//                         : "opacity-0 invisible -translate-y-2"
//                     }`}
//                   >
//                     {menu.children.map((child, idx) => (
//                       <a
//                         key={idx}
//                         href={child.url}
//                         className="block px-4 py-2 hover:bg-gray-100"
//                       >
//                         {child.title}
//                       </a>
//                     ))}
//                   </div>
//                 )}
//               </div>
//             ))}
//           </nav> */}

//           {/* --- Mobile Drawer Button --- */}
//           <button className="md:hidden p-2" onClick={() => setDrawerOpen(true)}>
//             <Menu size={24} />
//           </button>
//         </div>
//       </div>

//       {/* ===== Mobile Drawer ===== */}
//       <div
//         className={`fixed top-0 right-0 h-full w-3/4 sm:w-1/2 bg-white shadow-lg z-50 transform transition-transform duration-300 ${
//           drawerOpen ? "translate-x-0" : "translate-x-full"
//         }`}
//       >
//         <div className="flex justify-between items-center p-4 border-b">
//           <h2 className="text-lg font-semibold text-gray-800">Menu</h2>
//           <button onClick={() => setDrawerOpen(false)}>
//             <X size={24} />
//           </button>
//         </div>

//         <nav className="flex flex-col p-4 space-y-3 text-gray-700 font-medium">
//           {menuData.map((menu) => (
//             <div key={menu._id}>
//               {menu.children?.length > 0 ? (
//                 <>
//                   <button
//                     onClick={() => toggleDropdown(menu._id)}
//                     className="flex justify-between w-full"
//                   >
//                     {menu.title} <ChevronDown size={16} />
//                   </button>
//                   {openDropdown === menu._id && (
//                     <div className="ml-4 mt-2 space-y-2 text-sm flex flex-col">
//                       {menu.children.map((child, idx) => (
//                         <a
//                           key={idx}
//                           href={child.url}
//                           onClick={() => setDrawerOpen(false)}
//                           className="block py-1 hover:text-[#c03ca0]"
//                         >
//                           {child.title}
//                         </a>
//                       ))}
//                     </div>
//                   )}
//                 </>
//               ) : (
//                 <a
//                   href={menu.url}
//                   onClick={() => setDrawerOpen(false)}
//                   className="hover:text-[#c03ca0]"
//                 >
//                   {menu.title}
//                 </a>
//               )}
//             </div>
//           ))}
//         </nav>
//       </div>

//       {/* Overlay */}
//       {drawerOpen && (
//         <div
//           className="fixed inset-0 bg-black/30 z-40"
//           onClick={() => setDrawerOpen(false)}
//         ></div>
//       )}
//     </header>
//   );
// };

// export default Header;





















// import React, { useState } from "react";
// import { Menu, X, ChevronDown } from "lucide-react";
// import { Swiper, SwiperSlide } from "swiper/react";
// import { Autoplay, Navigation, EffectFade } from "swiper/modules";
// import "swiper/css";
// import "swiper/css/navigation";
// import "swiper/css/effect-fade";
// import logo from "../assets/logo.webp";

// const Header = () => {
//   const [drawerOpen, setDrawerOpen] = useState(false);
//   const [openDropdown, setOpenDropdown] = useState(null);

//   const toggleDropdown = (menu) => {
//     setOpenDropdown(openDropdown === menu ? null : menu);
//   };

//   return (
//     <header className="w-full">
//       {/* ===== Announcement Bar ===== */}
//       <div className="bg-[#c03ca0] text-white text-sm relative">
//         <div className="max-w-7xl mx-auto px-4 flex items-center justify-center h-8 relative">
//           <Swiper
//             modules={[Autoplay, Navigation, EffectFade]}
//             autoplay={{
//               delay: 3000,
//               disableOnInteraction: false,
//               pauseOnMouseEnter: true,
//             }}
//             navigation={{
//               nextEl: ".swiper-button-next-custom",
//               prevEl: ".swiper-button-prev-custom",
//             }}
//             loop={true}
//             effect="fade"
//             fadeEffect={{ crossFade: true }}
//             allowTouchMove={true}
//             speed={600}
//             className="w-[90%] text-center select-none z-10"
//           >
//             <SwiperSlide className="flex justify-center items-center">
//               SUBSCRIBE TO RECEIVE OFFERS
//             </SwiperSlide>
//             <SwiperSlide className="flex justify-center items-center">
//               FREE SHIPPING ON ORDERS OVER $50
//             </SwiperSlide>
//             <SwiperSlide className="flex justify-center items-center">
//               PLANT-BASED PRODUCTS YOU'LL LOVE ❤️
//             </SwiperSlide>
//           </Swiper>

//           {/* Arrows */}
//           <div className="swiper-button-prev-custom absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 cursor-pointer text-white text-base opacity-80 hover:opacity-100 z-20 select-none">
//             &#10094;
//           </div>
//           <div className="swiper-button-next-custom absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 cursor-pointer text-white text-base opacity-80 hover:opacity-100 z-20 select-none">
//             &#10095;
//           </div>
//         </div>
//       </div>

//       {/* ===== Main Navbar ===== */}
//       <div className="bg-white shadow-sm relative z-50">
//         <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-16">
//           {/* Logo */}
//           <div className="flex items-center">
//             <img src={logo} alt="Logo" className="h-10 w-auto" />
//           </div>

//           {/* Desktop Menu */}
//           <nav className="hidden md:flex space-x-8 text-gray-700 font-medium ml-auto relative">
//             {/* About */}
//             <div
//               className="relative"
//               onMouseEnter={() => setOpenDropdown("about")}
//               onMouseLeave={() => setOpenDropdown(null)}
//             >
//               <button className="flex items-center hover:text-[#c03ca0]">
//                 About <ChevronDown size={16} className="ml-1" />
//               </button>

//               <div
//                 className={`absolute left-0 mt-2 bg-white shadow-lg rounded-md py-2 w-40 transition-all duration-200 ${
//                   openDropdown === "about"
//                     ? "opacity-100 visible translate-y-0"
//                     : "opacity-0 invisible -translate-y-2"
//                 }`}
//               >
//                 <a
//                   href="/our-story"
//                   className="block px-4 py-2 hover:bg-gray-100"
//                 >
//                   Our Story
//                 </a>
//                 <a
//                   href="/our-impact"
//                   className="block px-4 py-2 hover:bg-gray-100"
//                 >
//                   Our Impact
//                 </a>
//               </div>
//             </div>

//             <a href="/products" className="hover:text-[#c03ca0]">
//               Our Products
//             </a>
//             <a href="/faq" className="hover:text-[#c03ca0]">
//               FAQ
//             </a>
//             <a href="/store-locator" className="hover:text-[#c03ca0]">
//               Store Locator
//             </a>

//             {/* Plant-Based Switch */}
//             <div
//               className="relative"
//               onMouseEnter={() => setOpenDropdown("plant")}
//               onMouseLeave={() => setOpenDropdown(null)}
//             >
//               <button className="flex items-center hover:text-[#c03ca0]">
//                 Plant-Based Switch <ChevronDown size={16} className="ml-1" />
//               </button>

//               <div
//                 className={`absolute left-0 mt-2 bg-white shadow-lg rounded-md py-2 w-44 transition-all duration-200 ${
//                   openDropdown === "plant"
//                     ? "opacity-100 visible translate-y-0"
//                     : "opacity-0 invisible -translate-y-2"
//                 }`}
//               >
//                 <a
//                   href="/podcast"
//                   className="block px-4 py-2 hover:bg-gray-100"
//                 >
//                   The Podcast
//                 </a>
//                 <a
//                   href="/book"
//                   className="block px-4 py-2 hover:bg-gray-100"
//                 >
//                   The Book
//                 </a>
//                 <a
//                   href="/app"
//                   className="block px-4 py-2 hover:bg-gray-100"
//                 >
//                   The App
//                 </a>
//               </div>
//             </div>

//             <a href="/blogs" className="hover:text-[#c03ca0]">
//               Recipes & Lifestyle
//             </a>
//             <a href="/contact" className="hover:text-[#c03ca0]">
//               Contact
//             </a>
//           </nav>

//           {/* Mobile Drawer Button */}
//           <button className="md:hidden p-2" onClick={() => setDrawerOpen(true)}>
//             <Menu size={24} />
//           </button>
//         </div>
//       </div>

//       {/* ===== Mobile Drawer ===== */}
//       <div
//         className={`fixed top-0 right-0 h-full w-3/4 sm:w-1/2 bg-white shadow-lg z-50 transform transition-transform duration-300 ${
//           drawerOpen ? "translate-x-0" : "translate-x-full"
//         }`}
//       >
//         <div className="flex justify-between items-center p-4 border-b">
//           <h2 className="text-lg font-semibold text-gray-800">Menu</h2>
//           <button onClick={() => setDrawerOpen(false)}>
//             <X size={24} />
//           </button>
//         </div>

//         <nav className="flex flex-col p-4 space-y-3 text-gray-700 font-medium">
//           {/* About */}
//           <div>
//             <button
//               onClick={() => toggleDropdown("about")}
//               className="flex justify-between w-full"
//             >
//               About <ChevronDown size={16} />
//             </button>
//             {openDropdown === "about" && (
//               <div className="ml-4 mt-2 space-y-2 text-sm flex flex-col">
//                 <a
//                   href="/our-story"
//                   className="block py-1 hover:text-[#c03ca0]"
//                   onClick={() => setDrawerOpen(false)}
//                 >
//                   Our Story
//                 </a>
//                 <a
//                   href="/our-impact"
//                   className="block py-1 hover:text-[#c03ca0]"
//                   onClick={() => setDrawerOpen(false)}
//                 >
//                   Our Impact
//                 </a>
//               </div>
//             )}
//           </div>

//           <a href="/products" onClick={() => setDrawerOpen(false)}>
//             Our Products
//           </a>
//           <a href="/faq" onClick={() => setDrawerOpen(false)}>
//             FAQ
//           </a>
//           <a href="/store-locator" onClick={() => setDrawerOpen(false)}>
//             Store Locator
//           </a>

//           {/* Plant-Based Switch */}
//           <div>
//             <button
//               onClick={() => toggleDropdown("plant")}
//               className="flex justify-between w-full"
//             >
//               Plant-Based Switch <ChevronDown size={16} />
//             </button>
//             {openDropdown === "plant" && (
//               <div className="ml-4 mt-2 space-y-2 text-sm flex flex-col">
//                 <a
//                   href="/podcast"
//                   className="block py-1 hover:text-[#c03ca0]"
//                   onClick={() => setDrawerOpen(false)}
//                 >
//                   The Podcast
//                 </a>
//                 <a
//                   href="/book"
//                   className="block py-1 hover:text-[#c03ca0]"
//                   onClick={() => setDrawerOpen(false)}
//                 >
//                   The Book
//                 </a>
//                 <a
//                   href="/app"
//                   className="block py-1 hover:text-[#c03ca0]"
//                   onClick={() => setDrawerOpen(false)}
//                 >
//                   The App
//                 </a>
//               </div>
//             )}
//           </div>

//           <a href="/blogs" onClick={() => setDrawerOpen(false)}>
//             Recipes & Lifestyle
//           </a>
//           <a href="/contact" onClick={() => setDrawerOpen(false)}>
//             Contact
//           </a>
//         </nav>
//       </div>

//       {/* Drawer Overlay */}
//       {drawerOpen && (
//         <div
//           className="fixed inset-0 bg-black/30 z-40"
//           onClick={() => setDrawerOpen(false)}
//         ></div>
//       )}
//     </header>
//   );
// };

// export default Header;






















// import React, { useState } from "react";
// import { Menu, X } from "lucide-react";
// import { Swiper, SwiperSlide } from "swiper/react";
// import { Autoplay, Navigation, EffectFade } from "swiper/modules";
// import "swiper/css";
// import "swiper/css/navigation";
// import "swiper/css/effect-fade";
// import logo from "../assets/logo.webp";

// const Header = () => {
//   const [drawerOpen, setDrawerOpen] = useState(false);

//   return (
//     <header className="w-full">
//       {/* ===== Announcement Bar ===== */}
//       <div className="bg-[#c03ca0] text-white text-sm relative">
//         <div className="max-w-7xl mx-auto px-4 flex items-center justify-center h-8 relative">
//           <Swiper
//             modules={[Autoplay, Navigation, EffectFade]}
//             autoplay={{
//               delay: 3000,
//               disableOnInteraction: false,
//               pauseOnMouseEnter: true,
//             }}
//             navigation={{
//               nextEl: ".swiper-button-next-custom",
//               prevEl: ".swiper-button-prev-custom",
//             }}
//             loop={true}
//             effect="fade"
//             fadeEffect={{ crossFade: true }}
//             allowTouchMove={true}
//             speed={600}
//             className="w-[90%] text-center select-none z-10"
//           >
//             <SwiperSlide className="flex justify-center items-center">
//               SUBSCRIBE TO RECEIVE OFFERS
//             </SwiperSlide>
//             <SwiperSlide className="flex justify-center items-center">
//               FREE SHIPPING ON ORDERS OVER $50
//             </SwiperSlide>
//             <SwiperSlide className="flex justify-center items-center">
//               PLANT-BASED PRODUCTS YOU'LL LOVE ❤️
//             </SwiperSlide>
//           </Swiper>

//           {/* Arrows (above everything) */}
//           <div className="swiper-button-prev-custom absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 cursor-pointer text-white text-base opacity-80 hover:opacity-100 z-20 select-none">
//             &#10094;
//           </div>
//           <div className="swiper-button-next-custom absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 cursor-pointer text-white text-base opacity-80 hover:opacity-100 z-20 select-none">
//             &#10095;
//           </div>
//         </div>
//       </div>

//       {/* ===== Main Navbar ===== */}
//       <div className="bg-white shadow-sm">
//         <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-16">
//           {/* Logo */}
//           <div className="flex items-center">
//             <img src={logo} alt="Logo" className="h-10 w-auto" />
//           </div>

//           {/* Desktop Menu */}
//           <nav className="hidden md:flex space-x-8 text-gray-700 font-medium ml-auto">
//             <a href="/" className="hover:text-[#c03ca0]">About</a>
//             <a href="/" className="hover:text-[#c03ca0]">Shop Products</a>
//             <a href="/blogs" className="hover:text-[#c03ca0]">Recipes & Lifestyle</a>
//             <a href="/" className="hover:text-[#c03ca0]">TV/Books</a>
//             <a href="/" className="hover:text-[#c03ca0]">Contact</a>
//           </nav>

//           {/* Mobile Drawer Button */}
//           <button
//             className="md:hidden p-2"
//             onClick={() => setDrawerOpen(true)}
//           >
//             <Menu size={24} />
//           </button>
//         </div>
//       </div>

//       {/* ===== Mobile Drawer ===== */}
//       <div
//         className={`fixed top-0 right-0 h-full w-3/4 sm:w-1/2 bg-white shadow-lg z-50 transform transition-transform duration-300 ${
//           drawerOpen ? "translate-x-0" : "translate-x-full"
//         }`}
//       >
//         <div className="flex justify-between items-center p-4 border-b">
//           <h2 className="text-lg font-semibold text-gray-800">Menu</h2>
//           <button onClick={() => setDrawerOpen(false)}>
//             <X size={24} />
//           </button>
//         </div>

//         <nav className="flex flex-col p-4 space-y-4 text-gray-700 font-medium">
//           <a href="/" onClick={() => setDrawerOpen(false)}>About</a>
//           <a href="/" onClick={() => setDrawerOpen(false)}>Shop Products</a>
//           <a href="/blog" onClick={() => setDrawerOpen(false)}>Recipes & Lifestyle</a>
//           <a href="/" onClick={() => setDrawerOpen(false)}>TV/Books</a>
//           <a href="/" onClick={() => setDrawerOpen(false)}>Contact</a>
//         </nav>
//       </div>

//       {/* Drawer Overlay */}
//       {drawerOpen && (
//         <div
//           className="fixed inset-0 bg-black/30 z-40"
//           onClick={() => setDrawerOpen(false)}
//         ></div>
//       )}
//     </header>
//   );
// };

// export default Header;
